import React, { useEffect, useState } from 'react';
import api from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Activity, ChevronDown, History, Loader2, Check } from 'lucide-react';
import SARButton from './SARButton';

const SiteStatePanel = ({ siteCode, initialState, initialHistory, siteInfo }) => {
  const { user } = useAuth();
  const canWrite = user && (user.role === 'admin' || user.role === 'gestor');
  const [states, setStates] = useState([]);
  const [current, setCurrent] = useState(initialState || null);
  const [history, setHistory] = useState(initialHistory || []);
  const [note, setNote] = useState('');
  const [showPicker, setShowPicker] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/site-states').then((r) => setStates(r.data || []));
  }, []);

  useEffect(() => {
    if (!siteCode) return;
    api.get(`/sites/${encodeURIComponent(siteCode)}/state-history`)
      .then((r) => {
        setCurrent(r.data.current_state || null);
        setHistory(r.data.history || []);
      })
      .catch(() => { /* first visit: no history yet */ });
  }, [siteCode]);

  const currentMeta = states.find((s) => s.id === current);

  const changeState = async (newState) => {
    setSaving(true);
    try {
      const r = await api.patch(`/sites/${encodeURIComponent(siteCode)}/state`, {
        state: newState, note: note.trim(),
      });
      setCurrent(r.data.state);
      if (r.data.history_entry) setHistory([r.data.history_entry, ...history]);
      setNote(''); setShowPicker(false);
      toast.success('Estado actualizado');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error cambiando estado');
    } finally { setSaving(false); }
  };

  if (!siteCode) return null;

  return (
    <div className="bg-black/40 border border-white/10 rounded-2xl p-4" data-testid="site-state-panel">
      <div className="flex items-center gap-3">
        <Activity className="text-[#A3FF12]" size={16}/>
        <div className="flex-1">
          <div className="text-[10px] font-black tracking-widest text-[#A3FF12]">ESTADO DEL SITE</div>
          <button
            onClick={() => canWrite && setShowPicker(!showPicker)}
            disabled={!canWrite}
            data-testid="btn-change-state"
            className={`inline-flex items-center gap-2 mt-1 px-3 py-1.5 rounded-full font-black text-sm border ${canWrite ? 'hover:opacity-80' : ''}`}
            style={{
              backgroundColor: (currentMeta?.color || '#94a3b8') + '20',
              color: currentMeta?.color || '#94a3b8',
              borderColor: (currentMeta?.color || '#94a3b8') + '55',
            }}
          >
            {currentMeta?.label || (current ? current : 'Sin estado')}
            {canWrite && <ChevronDown size={14}/>}
          </button>
        </div>
        {history.length > 0 && (
          <button onClick={() => setShowHistory(!showHistory)}
            className="text-white/40 hover:text-white p-1 inline-flex items-center gap-1 text-[10px] font-bold"
            data-testid="btn-toggle-history">
            <History size={12}/> {history.length}
          </button>
        )}
        {siteInfo?.lat && siteInfo?.lng && (
          <SARButton lat={siteInfo.lat} lng={siteInfo.lng} address={siteInfo.address}
            height_m={siteInfo.height_m || null}
            site_id={siteInfo.id || siteInfo.code || null}
            site_name={siteInfo.name || siteInfo.code || ''}
            afecciones_report={siteInfo.afecciones_report || null}
            variant="secondary"/>
        )}
      </div>

      {showPicker && canWrite && (
        <div className="mt-3 space-y-2" data-testid="state-picker">
          <input value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="Nota opcional del cambio (motivo, blocker, etc.)"
            className="w-full bg-black border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white outline-none focus:border-[#A3FF12]/50"/>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
            {states.map((s) => (
              <button key={s.id} onClick={() => changeState(s.id)} disabled={saving || s.id === current}
                data-testid={`state-option-${s.id}`}
                className="text-[10px] font-black uppercase tracking-widest px-2 py-1.5 rounded-lg border transition-all disabled:opacity-40"
                style={{
                  backgroundColor: s.id === current ? s.color + '30' : s.color + '15',
                  color: s.color,
                  borderColor: s.color + '55',
                }}>
                {saving && <Loader2 className="inline animate-spin mr-1" size={10}/>}
                {s.id === current && <Check className="inline mr-1" size={10}/>}
                {s.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {showHistory && history.length > 0 && (
        <div className="mt-3 border-t border-white/10 pt-2 space-y-1" data-testid="state-history">
          {history.slice(0, 10).map((h, idx) => (
            <div key={idx} className="text-[11px] flex items-start gap-2">
              <span className="text-white/40 flex-shrink-0">{new Date(h.changed_at).toLocaleDateString('es-ES')}</span>
              <span className="text-white/60 flex-1">
                {h.from_state ? `${h.from_state} → ` : ''}<b className="text-white">{h.to_state}</b>
                {h.note && <span className="text-white/40 italic"> · {h.note}</span>}
              </span>
              <span className="text-white/40 flex-shrink-0">{h.changed_by_name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SiteStatePanel;
